import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "./use-session";
import { useProfile } from "./use-profile";
import { useQuery } from "@tanstack/react-query";
import { useTenant } from "@/platform/tenants/use-tenant";
import { useTenantSubscription } from "@/platform/billing/use-subscription";
import { fetchUspProfile } from "@/tenants/usp/features/profile/api";

interface AuthGuardProps {
  children: ReactNode;
}

function FullPageLoading() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
      Carregando…
    </div>
  );
}

/**
 * Bloqueia acesso para usuários NÃO autenticados.
 *
 * Onboarding gate: o `einstein_profiles.onboarding_completed` só vale para o
 * tenant Einstein. Para USP, o onboarding fica em `usp_profiles` e é
 * checado pelo guard do tenant; aqui só forçamos onboarding quando a rota
 * É Einstein. Para rotas USP ou platform-level (sem tenant), apenas
 * checa autenticação.
 */
export function RequireAuth({ children }: AuthGuardProps) {
  const location = useLocation();
  const tenant = useTenant();
  const { session, loading: sessionLoading } = useSession();
  const { profile, loading: profileLoading } = useProfile();

  if (sessionLoading) return <FullPageLoading />;
  if (!session) {
    return <Navigate to="/entrar" replace state={{ from: location.pathname }} />;
  }
  if (profileLoading) return <FullPageLoading />;

  const isEinsteinRoute = tenant?.slug === "einstein";
  const onEinsteinOnboarding = location.pathname === "/onboarding";

  if (isEinsteinRoute) {
    // profile null pode acontecer brevemente quando o trigger de signup ainda
    // não rodou. Deixamos passar (children renderiza) — quando o profile
    // chegar, o efeito de re-render aplica o redirect adequado.
    if (profile && !profile.onboarding_completed && !onEinsteinOnboarding) {
      return <Navigate to="/onboarding" replace />;
    }
    if (profile?.onboarding_completed && onEinsteinOnboarding) {
      return <Navigate to="/painel" replace />;
    }
  }

  return <>{children}</>;
}

/**
 * Bloqueia acesso para usuários autenticados.
 * Útil em /entrar e /cadastro — quem já tem sessão vai pro painel/onboarding.
 */
export function RequireGuest({ children }: AuthGuardProps) {
  const { session, loading } = useSession();
  if (loading) return <FullPageLoading />;
  if (session) return <Navigate to="/painel" replace />;
  return <>{children}</>;
}

/**
 * Hard gate completo do tenant USP:
 *  1. Autenticação (RequireAuth)
 *  2. Licença ativa (active|trialing) em USP → senão /precos
 *  3. Onboarding USP concluído → senão /usp/onboarding
 *
 * Admins passam direto. Usado nas rotas pagas /usp/*.
 */
export function RequireSubscription({ children }: AuthGuardProps) {
  const tenant = useTenant();
  const location = useLocation();
  const { session } = useSession();
  const { profile } = useProfile();
  const sub = useTenantSubscription(tenant?.slug ?? "usp");
  const userId = session?.user.id;

  const uspOnboardingQuery = useQuery({
    queryKey: ["usp", "profile", "onboarding-status", userId],
    queryFn: () => fetchUspProfile(userId!),
    enabled: !!userId && tenant?.slug === "usp",
    staleTime: 60_000,
  });

  const onUspOnboarding = location.pathname === "/usp/onboarding";
  const uspOnboardingCompleted =
    tenant?.slug === "usp" ? !!uspOnboardingQuery.data?.onboarding_completed : true;
  const uspOnboardingLoading =
    tenant?.slug === "usp" && uspOnboardingQuery.isLoading;

  return (
    <RequireAuth>
      <SubscriptionGate
        tenantSlug={tenant?.slug ?? null}
        isAdmin={Boolean(profile?.is_admin)}
        isActive={sub.isActive}
        isLoading={sub.isLoading || uspOnboardingLoading}
        uspOnboardingCompleted={uspOnboardingCompleted}
        onUspOnboarding={onUspOnboarding}
      >
        {children}
      </SubscriptionGate>
    </RequireAuth>
  );
}

function SubscriptionGate({
  tenantSlug,
  isAdmin,
  isActive,
  isLoading,
  uspOnboardingCompleted,
  onUspOnboarding,
  children,
}: {
  tenantSlug: string | null;
  isAdmin: boolean;
  isActive: boolean;
  isLoading: boolean;
  uspOnboardingCompleted: boolean;
  onUspOnboarding: boolean;
  children: ReactNode;
}) {
  if (isAdmin) return <>{children}</>;
  if (isLoading) return <FullPageLoading />;
  if (!isActive) {
    return <Navigate to="/precos" replace state={{ requireSubscription: tenantSlug }} />;
  }
  // Tem licença mas ainda não completou onboarding USP → manda pra lá.
  if (tenantSlug === "usp" && !uspOnboardingCompleted && !onUspOnboarding) {
    return <Navigate to="/usp/onboarding" replace />;
  }
  // Já completou e está na rota de onboarding → vai pro painel.
  if (tenantSlug === "usp" && uspOnboardingCompleted && onUspOnboarding) {
    return <Navigate to="/usp/painel" replace />;
  }
  return <>{children}</>;
}

/**
 * Bloqueia acesso para não-admins. Compõe sobre RequireAuth.
 */
export function RequireAdmin({ children }: AuthGuardProps) {
  const { session, loading: sessionLoading } = useSession();
  const { profile, loading: profileLoading } = useProfile();
  if (sessionLoading || profileLoading) return <FullPageLoading />;
  if (!session) return <Navigate to="/entrar" replace />;
  if (!profile?.is_admin) return <Navigate to="/painel" replace />;
  return <>{children}</>;
}
